/**
 * Centralized logout utility for consistent logout behavior
 * across all components in the Medical admin panel
 */

export const logoutUtils = {
  /**
   * Performs complete logout with cleanup
   * @param {Function} navigate - React Router navigate function
   * @param {Function} authLogout - Optional auth context logout function
   * @returns {Promise<boolean>} Success status
   */
  performLogout: async (navigate = null, authLogout = null) => {
    try {
      // Clear all authentication-related localStorage items
      const authKeys = ['token', 'user', 'authToken', 'adminToken'];
      authKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
        } catch (error) {
          console.warn(`Error removing ${key} from localStorage:`, error);
        }
      });

      // Clear session storage as well (if any auth data is stored there)
      try {
        sessionStorage.clear();
      } catch (error) {
        console.warn('Error clearing sessionStorage:', error);
      }

      // Call auth context logout if provided
      if (authLogout && typeof authLogout === 'function') {
        try {
          authLogout();
        } catch (error) {
          console.warn('Error calling auth context logout:', error);
        }
      }

      // Close any WebSocket connections if they exist
      try {
        if (window.websocket && window.websocket.close) {
          window.websocket.close();
        }
      } catch (error) {
        console.warn('Error closing WebSocket:', error);
      }

      // Navigate to login page if navigate function is provided
      if (navigate && typeof navigate === 'function') {
        try {
          navigate('/', { replace: true });
        } catch (error) {
          console.warn('Error navigating to login:', error);
          // Fallback to window.location if navigate fails
          window.location.href = '/';
        }
      } else {
        // Fallback navigation
        window.location.href = '/';
      }

      return true;
    } catch (error) {
      console.error('Logout error:', error);
      // Even if there's an error, try to navigate to login
      try {
        if (navigate) {
          navigate('/', { replace: true });
        } else {
          window.location.href = '/';
        }
      } catch (navError) {
        console.error('Navigation error during logout:', navError);
      }
      return false;
    }
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated: () => {
    try {
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      
      if (!token || !user) {
        return false;
      }

      // Basic token format validation
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) {
        return false;
      }

      // Basic expiration check
      try {
        const payload = JSON.parse(atob(tokenParts[1]));
        const currentTime = Date.now() / 1000;
        
        if (payload.exp && payload.exp < currentTime) {
          return false;
        }
      } catch (decodeError) {
        console.warn('Token decode warning:', decodeError);
      }

      return true;
    } catch (error) {
      console.error('Authentication check error:', error);
      return false;
    }
  },

  /**
   * Get current user data
   * @returns {Object|null} User data or null
   */
  getCurrentUser: () => {
    try {
      const userString = localStorage.getItem('user');
      return userString ? JSON.parse(userString) : null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  },

  /**
   * Get current auth token
   * @returns {string|null} Token or null
   */
  getToken: () => {
    try {
      return localStorage.getItem('token');
    } catch (error) {
      console.error('Error getting token:', error);
      return null;
    }
  },

  /**
   * Show logout confirmation dialog
   * @param {Function} onConfirm - Callback for confirmed logout
   * @returns {boolean} User confirmation
   */
  confirmLogout: (onConfirm) => {
    const confirmed = window.confirm('Are you sure you want to logout?');
    if (confirmed && onConfirm) {
      onConfirm();
    }
    return confirmed;
  }
};

export default logoutUtils;